"use client";

import * as React from "react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { motion } from "framer-motion";
import { Instagram, Twitter, Linkedin, Mail, Globe, Calendar, ExternalLink, X } from "lucide-react";
// BookingFlow is removed, so replace with a placeholder component
function BookingFlowFallback() {
  return <section className="flex flex-col items-center justify-center min-h-[300px] w-full px-6 py-12 text-center">
      <h3 className="text-xl font-semibold mb-2 text-foreground">Booking feature coming soon</h3>
      <p className="text-muted-foreground max-w-xs mx-auto">We're working on a premium scheduling experience. Please check back soon or contact me directly for appointments.</p>
    </section>;
}
export interface LinkInBioProps {
  profile?: {
    name: string;
    title: string;
    avatar: string;
    initials: string;
  };
  links?: {
    id: string;
    title: string;
    url: string;
    icon: React.ReactElement;
    isExternal: boolean;
  }[];
}
const defaultProfile = {
  name: "Alex Rivera",
  title: "Creative Director & Brand Strategist",
  avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face",
  initials: "AR"
};
const defaultLinks = [{
  id: "portfolio",
  title: "View My Portfolio",
  url: "https://alexrivera.design",
  icon: <Globe className="h-4 w-4" />,
  isExternal: true
}, {
  id: "instagram",
  title: "Follow on Instagram",
  url: "https://instagram.com/alexrivera",
  icon: <Instagram className="h-4 w-4" />,
  isExternal: true
}, {
  id: "linkedin",
  title: "Connect on LinkedIn",
  url: "https://linkedin.com/in/alexrivera",
  icon: <Linkedin className="h-4 w-4" />,
  isExternal: true
}, {
  id: "twitter",
  title: "Follow on Twitter",
  url: "https://twitter.com/alexrivera",
  icon: <Twitter className="h-4 w-4" />,
  isExternal: true
}, {
  id: "email",
  title: "Send me an Email",
  url: "mailto:hello@alexrivera.design",
  icon: <Mail className="h-4 w-4" />,
  isExternal: false
}] as any[];
export default function LinkInBio({
  profile = defaultProfile,
  links = defaultLinks
}: LinkInBioProps) {
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const handleLinkClick = (link: typeof defaultLinks[0]) => {
    if (link.isExternal) {
      window.open(link.url, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = link.url;
    }
  };
  return <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 flex items-center justify-center p-4">
      <div className="w-full max-w-md mx-auto">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }} className="text-center space-y-8">
          {/* Profile Section */}
          <div className="space-y-6">
            <motion.div initial={{
            scale: 0.8,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="relative inline-block">
              <Avatar className="h-32 w-32 mx-auto ring-4 ring-background shadow-2xl">
                <AvatarImage src={profile.avatar} alt={profile.name} className="object-cover" />
                <AvatarFallback className="text-2xl font-semibold bg-gradient-to-br from-primary/20 to-primary/10 text-primary">
                  {profile.initials}
                </AvatarFallback>
              </Avatar>
              
              {/* Subtle glow effect */}
              <div className="absolute inset-0 h-32 w-32 mx-auto rounded-full bg-gradient-to-br from-primary/10 to-transparent blur-xl -z-10" />
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.4
          }} className="space-y-2">
              <h1 className="text-2xl font-bold tracking-tight text-foreground">
                {profile.name}
              </h1>
              <p className="text-muted-foreground text-base leading-relaxed max-w-xs mx-auto">
                {profile.title}
              </p>
            </motion.div>
          </div>

          {/* Book Appointment Button - Prominent */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.6
        }}>
            <Sheet open={isBookingOpen} onOpenChange={setIsBookingOpen}>
              <SheetTrigger asChild>
                <Button size="lg" className="w-full h-14 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-base shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02]">
                  <Calendar className="mr-3 h-5 w-5" />
                  Book Appointment
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-4xl p-0 border-none">
                <div className="h-full overflow-auto">
                  <div className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b px-6 py-4 flex items-center justify-between">
                    <h2 className="text-lg font-semibold">Book Your Session</h2>
                    <Button variant="ghost" size="icon" onClick={() => setIsBookingOpen(false)} className="h-8 w-8">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="p-0">
  <BookingFlowFallback />
                </div>
                </div>
              </SheetContent>
            </Sheet>
          </motion.div>

          {/* Links Section */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.5,
          delay: 0.8
        }} className="space-y-3">
            {links.map((link, index) => <motion.div key={link.id} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.4,
            delay: 0.9 + index * 0.1,
            ease: "easeOut"
          }}>
                <Button variant="outline" size="lg" onClick={() => handleLinkClick(link)} className={cn("w-full h-12 rounded-full border-2 bg-card/50 backdrop-blur-sm", "hover:bg-accent hover:border-accent-foreground/20 hover:shadow-md", "transition-all duration-300 hover:scale-[1.02]", "text-foreground font-medium")}>
                  <div className="flex items-center justify-center gap-3">
                    {link.icon}
                    <span>{link.title}</span>
                    {link.isExternal && <ExternalLink className="h-3 w-3 opacity-60" />}
                  </div>
                </Button>
              </motion.div>)}
          </motion.div>

          {/* Footer */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.5,
          delay: 1.4
        }} className="pt-8 pb-4">
            <p className="text-xs text-muted-foreground/60">
              © 2024 {profile.name}. All rights reserved.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>;
}